// Formatting helpers. Cameroon writes amounts with a space as the thousands
// separator, so 250000 reads as "250 000 FCFA".

const grouped = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 });

export const formatXaf = (amount) => {
  if (amount === null || amount === undefined) return '-';
  return `${grouped.format(amount).replace(/ | /g, ' ')} FCFA`;
};

export const formatRent = (amount) => `${formatXaf(amount)} / month`;

/** Short enough for a map pin: 45 000, 850k, 45m, 2.5bn. */
export const formatCompact = (amount) => {
  if (amount === null || amount === undefined) return '-';
  const short = (value, unit) => `${value.toLocaleString('en', { maximumFractionDigits: 1 })}${unit}`;
  if (amount >= 1_000_000_000) return short(amount / 1_000_000_000, 'bn');
  if (amount >= 1_000_000) return short(amount / 1_000_000, 'm');
  if (amount >= 100_000) return short(amount / 1000, 'k');
  return grouped.format(amount).replace(/ | /g, ' ');
};

export const formatNumber = (value) => grouped.format(value ?? 0).replace(/ | /g, ' ');

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Two kinds of value arrive from the API and they must be treated differently.
 *
 * A bare calendar date - a tenancy start, a rent due date, a move-in date - has no
 * time and no zone. "2026-05-01" means the first of May wherever you read it, so it
 * is parsed as a local date. Handing it straight to Date() would read it as UTC
 * midnight, which renders as 30 April for anyone west of Greenwich: wrong, and
 * wrong on a signed agreement.
 *
 * A SQLite timestamp ("2026-08-01 19:55:18") is UTC and does have a time, so it
 * gets the T and the Z it needs. Safari refuses to parse it otherwise.
 */
const toDate = (value) => {
  if (!value) return null;

  if (typeof value === 'string') {
    if (DATE_ONLY.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    if (value.includes(' ')) {
      const date = new Date(`${value.replace(' ', 'T')}Z`);
      return Number.isNaN(date.getTime()) ? null : date;
    }
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDate = (value) => {
  const date = toDate(value);
  if (!date) return '-';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const formatDateTime = (value) => {
  const date = toDate(value);
  if (!date) return '-';
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const monthsBetween = (start, end) => {
  const startDate = toDate(start);
  const endDate = toDate(end);
  if (!startDate || !endDate || startDate > endDate) return 0;

  const startMonthIndex = startDate.getFullYear() * 12 + startDate.getMonth();
  const endMonthIndex = endDate.getFullYear() * 12 + endDate.getMonth();
  let months = endMonthIndex - startMonthIndex + 1;

  if (endDate.getDate() < startDate.getDate()) {
    months -= 1;
  }

  return Math.max(months, 0);
};

export const timeAgo = (value) => {
  const date = toDate(value);
  if (!date) return '';

  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';

  const steps = [
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.35, 'week'],
    [12, 'month']
  ];

  let amount = seconds / 60;
  let unit = 'minute';
  for (let i = 0; i < steps.length - 1; i += 1) {
    if (amount < steps[i + 1][0]) break;
    amount /= steps[i + 1][0];
    unit = steps[i + 1][1];
  }

  const rounded = Math.floor(amount);
  return `${rounded} ${unit}${rounded === 1 ? '' : 's'} ago`;
};

// Relative media URLs come back as /uploads/... and go through the dev proxy, so
// they need no rewriting. External links (YouTube thumbnails) pass through.
export const mediaUrl = (url) => url || null;

export const titleCase = (value) =>
  (value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());

export const initials = (name) =>
  (name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

export const pluralise = (count, singular, plural) =>
  `${formatNumber(count)} ${count === 1 ? singular : plural ?? `${singular}s`}`;

// The rent on a lease is the amount for one billing period, so anything that
// says "45 000 FCFA a month" must say "600 000 FCFA a year" on a yearly lease.
const RENT_PERIOD_NOUNS = { monthly: 'month', quarterly: 'quarter', yearly: 'year' };
export const rentPeriodNoun = (billingPeriod) => RENT_PERIOD_NOUNS[billingPeriod] || 'month';

const RENT_PERIOD_MONTHS = { monthly: 1, quarterly: 3, yearly: 12 };
export const rentPeriodMonths = (billingPeriod) => RENT_PERIOD_MONTHS[billingPeriod] || 1;
